import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, message, Modal } from 'antd';
import { CloseXIcon } from 'icons';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import { getPlatform } from 'modules/global';

import { Visible } from 'components';

import { useExitFeedback, useFeedbackReasons, useSendFeedback } from '../hooks';
import { setOpen } from '../store';

const { TextArea } = Input;

export const ExitFeedBackModal = () => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const open = useAppSelector(({ proExitModal }) => proExitModal.open);
  const [selectedReason, setSelectedReason] = useState<number>();
  const [writtenReason, setWrittenReason] = useState<string>('');
  const { mutate, isLoading } = useSendFeedback();
  const { reasons, isLoading: reasonsLoading } = useFeedbackReasons();
  const { continueNavigation } = useExitFeedback();

  const [countdown, setCountdown] = useState<number | null>(null);
  const [canClose, setCanClose] = useState(false);

  const onCheckboxChange = (checkedValues: number) => {
    setSelectedReason(checkedValues);
  };

  const onClose = () => {
    if (!canClose) return;
    dispatch(setOpen(false));
    continueNavigation();
  };

  const onSubmit = async () => {
    if (!selectedReason && !writtenReason) return;

    const application = {
      payment_feedback_reasons_id: selectedReason,
      custom_feedback: writtenReason,
      platform: getPlatform()
    };

    try {
      await mutate(application, {
        onSuccess: () => {
          continueNavigation();
          dispatch(setOpen(false));
        }
      });
    } catch (error) {
      message.error(t('nomalum_xatolik_yuz_berdi'));
    }
  };

  useEffect(() => {
    if (open) {
      setCanClose(false);
      setCountdown(null);

      let total = 5;
      const interval = setInterval(() => {
        total -= 1;
        if (total <= 0) {
          setCanClose(true);
          setCountdown(null);
          clearInterval(interval);
        } else {
          setCountdown(total);
        }
      }, 1000);

      return () => clearInterval(interval);
    }

    return undefined;
  }, [open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width="31rem"
      closeIcon={false}
      closable={false}
      maskClosable={false}
      keyboard={false}
      loading={reasonsLoading}
      footer={[
        <Button
          loading={isLoading}
          disabled={!selectedReason && !writtenReason}
          key="submit"
          className="!py-4.5 !w-full !border-transparent !font-medium"
          type="primary"
          onClick={onSubmit}
        >
          {t('Yuborish')}
        </Button>
      ]}
      styles={{
        content: { borderRadius: '12px' }
      }}
      centered
    >
      <div className="absolute right-4 top-4">
        <Visible
          if={canClose}
          else={
            <Visible if={countdown !== null}>
              <div className="flex size-7 select-none items-center justify-center rounded-full bg-gray-100 text-gray-600">
                {countdown}
              </div>
            </Visible>
          }
        >
          <button
            onClick={onClose}
            className="flex size-7 cursor-pointer items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200"
          >
            <CloseXIcon color="#000" />
          </button>
        </Visible>
      </div>
      <h1 className="mb-5 text-center text-lg font-medium">{t('obuna_bolishdan_sizni_nima_toxtatdi')}</h1>
      <div className="space-y-3">
        {reasons?.map(item => (
          <div
            key={item.id}
            className={`flex cursor-pointer items-center rounded-xl border p-3 
              ${selectedReason === item.id ? 'border-green-500 bg-green-50' : ' border-gray-200 hover:border-green-500'}`}
            onClick={() => onCheckboxChange(item.id)}
          >
            <Checkbox
              checked={selectedReason === item.id}
              className="[&>.ant-checkbox-inner]:h-8 [&>.ant-checkbox-inner]:w-8 [&>.ant-checkbox-inner]:!rounded-full"
            />
            <span className="ml-3 font-medium text-gray-800">{item.reason}</span>
          </div>
        ))}
        <TextArea
          className="!border-gray-200 !bg-white hover:!border-green-500 focus:!border-green-500"
          rows={4}
          onChange={e => setWrittenReason(e.target.value)}
          placeholder={`${t('boshqa')}...`}
          maxLength={400}
        />
      </div>
    </Modal>
  );
};
